import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { PageHeader, Btn, Badge, dt } from "../components/common";
import { Modal, Field, TextArea } from "./crm/crmui";
import { toast } from "sonner";
import { Plus, Video, Trash2, UserCheck, XCircle, Send, Mail, CheckSquare, Square } from "lucide-react";
import { useAutoRefresh } from "../lib/autoRefresh";

const EMPTY = { assunto: "", inicio: "", fim: "", account_id: "", participantes: "", descricao: "", teams: true, responsavel_id: "", invite_client: false };

export default function Scheduling() {
  const { can } = useAuth();
  const isSuper = can("*");
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [m, setM] = useState(EMPTY);
  const [assign, setAssign] = useState(null); // meeting a reatribuir
  const [avail, setAvail] = useState(null); // {configured, busy, error} ou "loading"
  const [inviteEmail, setInviteEmail] = useState(null); // {meeting, para, assunto, html, contactos}
  const [selected, setSelected] = useState(new Set()); // ids seleccionados
  const [bulkAssign, setBulkAssign] = useState(null); // {responsavel_id, invite_client}
  const [filterEstado, setFilterEstado] = useState(""); // filtro rapido
  const [filterAccount, setFilterAccount] = useState("");

  // Carrega contactos da conta associada (para escolher destinatario diferente do principal)
  const loadAccountContacts = async (accountId) => {
    if (!accountId) return [];
    try {
      const { data } = await api.get(`/crm/accounts/${accountId}/emails`);
      return data.contactos || [];
    } catch { return []; }
  };

  const load = async () => {
    try {
      const [mt, acc, mem] = await Promise.all([
        api.get("/scheduling/meetings"),
        api.get("/crm/accounts"),
        api.get("/team/members").catch(() => ({ data: [] })),
      ]);
      setRows(mt.data); setAccounts(acc.data); setMembers(mem.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load); // eslint-disable-line

  // Pre-preencher via querystring (vindo de Atividades ou da ficha da conta)
  useEffect(() => {
    const account_id = params.get("account_id") || "";
    const assunto = params.get("assunto") || "";
    const descricao = params.get("descricao") || "";
    if (account_id || assunto) {
      setM({ ...EMPTY, account_id, assunto, descricao, invite_client: !!account_id });
      setShow(true);
      const next = new URLSearchParams(params);
      ["account_id", "assunto", "descricao"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    }
  }, []); // eslint-disable-line

  const toIso = (local) => (local ? new Date(local).toISOString() : "");
  const memberOf = (id) => members.find((x) => x.id === id);

  const checkAvailability = async () => {
    const resp = memberOf(m.responsavel_id);
    if (!resp?.email) return toast.error("Escolha primeiro o responsavel / agenda.");
    if (!m.inicio) return toast.error("Indique a data/hora de inicio.");
    const date = m.inicio.slice(0, 10);
    setAvail("loading");
    try {
      const { data } = await api.get("/scheduling/availability", { params: { user_email: resp.email, date } });
      setAvail(data);
    } catch (e) { setAvail({ configured: false }); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!m.assunto.trim() || !m.inicio || !m.fim) return toast.error("Assunto, inicio e fim obrigatorios.");
    const resp = memberOf(m.responsavel_id);
    try {
      const res = await api.post("/scheduling/meetings", {
        assunto: m.assunto, inicio: toIso(m.inicio), fim: toIso(m.fim),
        account_id: m.account_id || null,
        participantes: m.participantes ? m.participantes.split(",").map((s) => s.trim()).filter(Boolean) : [],
        descricao: m.descricao, teams: m.teams,
        organizer_user: resp?.email || "",
        assigned_to: resp?.id || "",
        assigned_email: resp?.email || "",
        assigned_nome: resp?.nome || resp?.email || "",
        invite_client: !!m.invite_client,
      });
      const sync = res.data.sync_estado;
      toast[sync === "sincronizado" ? "success" : "message"](`Reuniao criada (${sync}).`);
      setShow(false); setM(EMPTY);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doAssign = async () => {
    const resp = memberOf(assign.responsavel_id);
    if (!resp) return toast.error("Escolha um membro da equipa.");
    try {
      await api.patch(`/scheduling/meetings/${assign.id}/assign`, {
        assigned_to: resp.id, assigned_email: resp.email, assigned_nome: resp.nome || resp.email,
        invite_client: !!assign.invite_client,
        // Passa o email do convidado herdado do proprio agendamento (nao do contacto principal da conta)
        client_email: assign.client_email || "",
      });
      toast.success(`Reuniao atribuida a ${resp.nome || resp.email}. Convite Teams reenviado${assign.invite_client ? ` (cliente ${assign.client_email || "principal"} convidado)` : ""}.`);
      setAssign(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openAssign = async (r) => {
    // O email do convidado por defeito e o que veio no proprio agendamento (client_email
    // gravado quando o pedido chegou do formulario). Se por algum motivo estiver vazio,
    // vamos buscar o primeiro participante nao interno; e por fim o contacto principal da conta.
    let clientEmail = (r.client_email || "").trim();
    if (!clientEmail && (r.participantes || []).length) {
      // Escolher o primeiro participante que nao seja um membro da equipa
      const emails = (r.participantes || []).filter((e) => !members.some((m) => (m.email || "").toLowerCase() === e.toLowerCase()));
      if (emails.length) clientEmail = emails[0];
    }
    const contactos = await loadAccountContacts(r.account_id);
    if (!clientEmail && contactos.length) {
      const principal = contactos.find((c) => c.principal) || contactos[0];
      clientEmail = principal?.email || "";
    }
    setAssign({
      id: r.id,
      responsavel_id: r.assigned_to || "",
      invite_client: true,
      client_email: clientEmail,
      contactos, // opcoes para o dropdown de convidado
    });
  };

  const openInviteEmail = async (meeting) => {
    // Preferencia: email guardado no agendamento (dado por quem pediu a reuniao),
    // depois primeiro participante nao interno, depois contacto principal da conta.
    const contactos = await loadAccountContacts(meeting.account_id);
    let para = (meeting.client_email || "").trim();
    if (!para && (meeting.participantes || []).length) {
      const emails = (meeting.participantes || []).filter((e) => !members.some((m) => (m.email || "").toLowerCase() === e.toLowerCase()));
      if (emails.length) para = emails[0];
    }
    if (!para && contactos.length) {
      const principal = contactos.find((c) => c.principal) || contactos[0];
      para = principal?.email || "";
    }
    if (!para) {
      const acc = accounts.find((a) => a.id === meeting.account_id);
      para = acc?.email || "";
    }
    const dataInicio = meeting.inicio ? new Date(meeting.inicio).toLocaleString("pt-PT") : "";
    const dataFim = meeting.fim ? new Date(meeting.fim).toLocaleString("pt-PT") : "";
    const linkTeams = meeting.join_url ? `\n\nLink Microsoft Teams:\n${meeting.join_url}` : "";
    const corpo = `Ola,\n\nConfirmamos o agendamento da reuniao "${meeting.assunto}".\n\nInicio: ${dataInicio}\nFim: ${dataFim}${linkTeams}\n\nSe precisar de reagendar, responda a este email.\n\nCumprimentos,\nEquipa Cogniseg`;
    setInviteEmail({
      meeting,
      para,
      assunto: `Convite: ${meeting.assunto}`,
      corpo,
      contactos,
    });
  };
  const sendInviteEmail = async () => {
    if (!inviteEmail?.para) return toast.error("Indique o destinatario.");
    try {
      const html = (inviteEmail.corpo || "").split("\n").map((l) => `<p>${l}</p>`).join("");
      const { data } = await api.post(`/scheduling/meetings/${inviteEmail.meeting.id}/send-invite-email`, {
        para: inviteEmail.para, assunto: inviteEmail.assunto, html,
        incluir_teams_link: false, incluir_data: false,
      });
      if (data?.estado === "enviado") toast.success("Convite enviado por email.");
      else toast.message(`Estado do envio: ${data?.estado || "?"}${data?.erro ? " - " + data.erro : ""}`);
      setInviteEmail(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/scheduling/meetings/${id}`); toast.success("Reuniao cancelada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removePermanent = async (id) => {
    if (!window.confirm("Eliminar definitivamente esta reuniao? Esta accao nao pode ser revertida.")) return;
    try { await api.delete(`/scheduling/meetings/${id}/permanent`); toast.success("Reuniao eliminada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cols = [
    { key: "assunto", label: "Assunto", render: (r) => <span className="font-semibold text-navy">{r.assunto}</span> },
    { key: "inicio", label: "Inicio", render: (r) => dt(r.inicio) },
    { key: "fim", label: "Fim", render: (r) => dt(r.fim) },
    { key: "responsavel", label: "Responsavel / agenda", render: (r) => <span className="text-xs">{r.assigned_nome || r.assigned_email || <span className="text-muted2">- default -</span>}</span> },
    { key: "participantes", label: "Convidados", render: (r) => <span className="text-xs">{(r.participantes || []).join(", ") || "-"}</span> },
    { key: "estado", label: "Estado", render: (r) => <Badge value={r.estado} /> },
    { key: "sync_estado", label: "O365", render: (r) => <span className={`text-xs ${r.sync_estado === "sincronizado" ? "text-green-600" : "text-muted2"}`}>{r.sync_estado}</span> },
    { key: "join_url", label: "Teams", render: (r) => r.join_url ? <a href={r.join_url} target="_blank" rel="noreferrer" className="text-blue text-xs hover:underline inline-flex items-center gap-1" data-testid={`meeting-join-${r.id}`}><Video size={13} /> Entrar</a> : "-" },
    {
      key: "acoes", label: "", render: (r) => (
        <div className="flex items-center gap-1">
          <button onClick={() => openAssign(r)} data-testid={`meeting-assign-${r.id}`} title="Atribuir a membro da equipa" className="text-blue hover:bg-blue-50 rounded p-1"><UserCheck size={15} /></button>
          <button onClick={() => openInviteEmail(r)} data-testid={`meeting-invite-email-${r.id}`} title="Enviar convite personalizado por email" className="text-green-700 hover:bg-green-50 rounded p-1"><Mail size={15} /></button>
          {r.estado !== "cancelada" && <button onClick={() => cancel(r.id)} data-testid={`meeting-cancel-${r.id}`} title="Cancelar reuniao" className="text-amber-600 hover:bg-amber-50 rounded p-1"><XCircle size={15} /></button>}
          {isSuper && <button onClick={() => removePermanent(r.id)} data-testid={`meeting-delete-${r.id}`} title="Eliminar definitivamente (super admin)" className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button>}
        </div>
      )
    },
  ];

  // Filtragem + agregado dos ids visiveis (para o select-all so afectar linhas visiveis)
  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterEstado && r.estado !== filterEstado) return false;
      if (filterAccount && r.account_id !== filterAccount) return false;
      return true;
    });
  }, [rows, filterEstado, filterAccount]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (visibleRows.every((r) => selected.has(r.id)) && visibleRows.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleRows.map((r) => r.id)));
    }
  };
  const clearSelection = () => setSelected(new Set());

  const bulkCancel = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Cancelar ${ids.length} agendamento(s)? A operacao actualiza tambem o M365 quando sincronizado.`)) return;
    let ok = 0, ko = 0;
    for (const id of ids) {
      try { await api.delete(`/scheduling/meetings/${id}`); ok++; } catch { ko++; }
    }
    toast[ko ? "message" : "success"](`Cancelamentos: ${ok} OK${ko ? `, ${ko} falharam` : ""}.`);
    clearSelection(); load();
  };
  const doBulkAssign = async () => {
    const ids = [...selected];
    const resp = members.find((u) => u.id === bulkAssign.responsavel_id);
    if (!resp) return toast.error("Escolha um membro da equipa.");
    let ok = 0, ko = 0;
    for (const id of ids) {
      try {
        await api.patch(`/scheduling/meetings/${id}/assign`, {
          assigned_to: resp.id, assigned_email: resp.email, assigned_nome: resp.nome || resp.email,
          invite_client: !!bulkAssign.invite_client,
        });
        ok++;
      } catch { ko++; }
    }
    toast[ko ? "message" : "success"](`Atribuidos ${ok} agendamento(s) a ${resp.nome || resp.email}${ko ? ` (${ko} falharam)` : ""}.`);
    setBulkAssign(null); clearSelection(); load();
  };

  return (
    <div>
      <PageHeader overline="Reunioes e demos (M19)" title="Agendamentos"
        action={<Btn onClick={() => { setShow(true); setAvail(null); }} testid="new-meeting-btn"><Plus size={16} /> Nova reuniao</Btn>} />
      <p className="text-xs text-muted2 mb-4">Visao global de todos os agendamentos. Sincroniza com Microsoft 365, Teams e a timeline de cada conta. Use as caixas de seleccao para atribuir ou cancelar varios de uma vez.</p>

      {/* Filtros rapidos */}
      <div className="mb-3 flex flex-wrap items-end gap-3 bg-white border border-tint rounded-lg p-3" data-testid="scheduling-filters">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted2 mb-1">Estado</label>
          <select value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)} data-testid="filter-estado" className="border border-tint rounded-md px-3 py-1.5 text-sm bg-white">
            <option value="">Todos</option>
            <option value="provisorio">Provisorio</option>
            <option value="confirmada">Confirmada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted2 mb-1">Conta</label>
          <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)} data-testid="filter-account" className="border border-tint rounded-md px-3 py-1.5 text-sm bg-white min-w-[220px]">
            <option value="">Todas</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </select>
        </div>
        {(filterEstado || filterAccount) && (
          <button onClick={() => { setFilterEstado(""); setFilterAccount(""); }} className="text-xs text-muted2 hover:text-navy underline" data-testid="filter-clear">Limpar filtros</button>
        )}
        <div className="ml-auto text-[11px] text-muted2" data-testid="scheduling-count">{visibleRows.length} / {rows.length} agendamentos</div>
      </div>

      {/* Barra de accoes em massa */}
      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 bg-blue/5 border border-blue/30 rounded-lg px-3 py-2" data-testid="bulk-actions-bar">
          <span className="text-sm font-semibold text-navy">{selected.size} seleccionado(s)</span>
          <button
            onClick={() => setBulkAssign({ responsavel_id: "", invite_client: true })}
            data-testid="bulk-assign-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-md border border-blue text-blue bg-white hover:bg-blue-50 px-2.5 py-1"
          ><UserCheck size={13} /> Atribuir a membro</button>
          <button
            onClick={bulkCancel}
            data-testid="bulk-cancel-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-md border border-amber-500 text-amber-700 bg-white hover:bg-amber-50 px-2.5 py-1"
          ><XCircle size={13} /> Cancelar todos</button>
          <button
            onClick={clearSelection}
            data-testid="bulk-clear-btn"
            className="text-xs text-muted2 hover:text-navy underline ml-auto"
          >Limpar seleccao</button>
        </div>
      )}

      {/* Tabela com seleccao multipla */}
      <div className="bg-white rounded-lg border border-tint overflow-hidden">
        <table className="w-full text-sm" data-testid="meetings-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-muted2">
            <tr>
              <th className="w-10 px-3 py-2">
                <button onClick={toggleAll} data-testid="select-all" title="Seleccionar todos (visiveis)" className="text-navy hover:text-blue">
                  {visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.id))
                    ? <CheckSquare size={16} />
                    : <Square size={16} />}
                </button>
              </th>
              {cols.map((c) => <th key={c.key} className="text-left px-3 py-2">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="text-center py-6 text-muted2 text-xs">Sem agendamentos para os filtros seleccionados.</td></tr>
            )}
            {visibleRows.map((r) => {
              const isSel = selected.has(r.id);
              return (
                <tr key={r.id} className={`border-t border-tint hover:bg-slate-50 ${isSel ? "bg-blue/5" : ""}`} data-testid={`meeting-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => toggle(r.id)} data-testid={`select-${r.id}`} className="text-navy hover:text-blue">
                      {isSel ? <CheckSquare size={16} className="text-blue" /> : <Square size={16} />}
                    </button>
                  </td>
                  {cols.map((c) => <td key={c.key} className="px-3 py-2">{c.render(r)}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={show} onClose={() => setShow(false)} title="Nova reuniao / demo" testid="meeting-modal">
        <Field label="Assunto" value={m.assunto} onChange={(v) => setM({ ...m, assunto: v })} testid="meeting-assunto" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Inicio</label>
            <input type="datetime-local" value={m.inicio} onChange={(e) => setM({ ...m, inicio: e.target.value })} data-testid="meeting-inicio" className="w-full px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Fim</label>
            <input type="datetime-local" value={m.fim} onChange={(e) => setM({ ...m, fim: e.target.value })} data-testid="meeting-fim" className="w-full px-3 py-2 border border-tint rounded-lg" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Conta / Lead (opcional)</label>
          <select value={m.account_id} onChange={(e) => setM({ ...m, account_id: e.target.value })} data-testid="meeting-account" className="w-full px-3 py-2 border border-tint rounded-lg bg-white">
            <option value="">- Sem conta -</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Responsavel / agenda (membro da equipa)</label>
          <select value={m.responsavel_id} onChange={(e) => { setM({ ...m, responsavel_id: e.target.value }); setAvail(null); }} data-testid="meeting-responsavel" className="w-full px-3 py-2 border border-tint rounded-lg bg-white">
            <option value="">- Organizador default -</option>
            {members.map((u) => <option key={u.id} value={u.id}>{u.nome || u.email} {u.email ? `(${u.email})` : ""}</option>)}
          </select>
          <p className="text-[11px] text-muted2 mt-1">A reuniao e criada na agenda deste membro e o convite Teams e-lhe enviado.</p>
          {m.responsavel_id && m.inicio && (
            <div className="mt-2">
              <button type="button" onClick={checkAvailability} data-testid="check-availability-btn" className="text-xs text-blue hover:underline">Ver disponibilidade no dia</button>
              {avail === "loading" && <span className="text-xs text-muted2 ml-2">a verificar...</span>}
              {avail && avail !== "loading" && !avail.configured && (
                <p className="text-[11px] text-amber-600 mt-1">Microsoft 365 nao configurado - nao e possivel verificar a disponibilidade automatica.</p>
              )}
              {avail && avail !== "loading" && avail.configured && (
                (avail.busy || []).length === 0
                  ? <p className="text-[11px] text-green-600 mt-1" data-testid="availability-free">Sem compromissos nesse dia - agenda livre.</p>
                  : <div className="text-[11px] text-slate-600 mt-1" data-testid="availability-busy">
                      Ocupado: {(avail.busy || []).map((b, i) => <span key={i} className="inline-block bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5 mr-1 mb-1">{(b.inicio || "").slice(11, 16)}-{(b.fim || "").slice(11, 16)}</span>)}
                    </div>
              )}
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={m.invite_client} onChange={(e) => setM({ ...m, invite_client: e.target.checked })} data-testid="meeting-invite-client" /> Convidar o cliente/conta (usa o email do contacto principal)</label>
        <Field label="Outros convidados (emails, separados por virgula)" value={m.participantes} onChange={(v) => setM({ ...m, participantes: v })} testid="meeting-participantes" />
        <TextArea label="Descricao" value={m.descricao} onChange={(v) => setM({ ...m, descricao: v })} testid="meeting-descricao" />
        <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={m.teams} onChange={(e) => setM({ ...m, teams: e.target.checked })} data-testid="meeting-teams" /> Criar reuniao Teams (online)</label>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn><Btn onClick={create} testid="meeting-save">Agendar</Btn></div>
      </Modal>

      <Modal open={!!assign} onClose={() => setAssign(null)} title="Atribuir reuniao a membro da equipa" testid="meeting-assign-modal">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Membro da equipa</label>
          <select value={assign?.responsavel_id || ""} onChange={(e) => setAssign({ ...assign, responsavel_id: e.target.value })} data-testid="assign-member" className="w-full px-3 py-2 border border-tint rounded-lg bg-white">
            <option value="">- Escolher -</option>
            {members.map((u) => <option key={u.id} value={u.id}>{u.nome || u.email} {u.email ? `(${u.email})` : ""}</option>)}
          </select>
          <p className="text-[11px] text-muted2 mt-1">O convite Teams sera reenviado para o novo responsavel.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-navy mt-3">
          <input type="checkbox" checked={!!assign?.invite_client} onChange={(e) => setAssign({ ...assign, invite_client: e.target.checked })} data-testid="assign-invite-client" />
          Convidar tambem o cliente
        </label>
        {assign?.invite_client && (
          <div className="mt-2 ml-6">
            <label className="block text-xs uppercase text-muted2 mb-1">Email do convidado (herdado do agendamento)</label>
            <input
              type="email"
              value={assign?.client_email || ""}
              onChange={(e) => setAssign({ ...assign, client_email: e.target.value })}
              placeholder="cliente@empresa.pt"
              data-testid="assign-client-email"
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm"
            />
            {(assign?.contactos || []).length > 0 && (
              <select
                value=""
                onChange={(e) => setAssign({ ...assign, client_email: e.target.value })}
                data-testid="assign-client-picker"
                className="mt-1 w-full px-3 py-2 border border-tint rounded-lg bg-white text-xs text-muted2"
              >
                <option value="">... ou escolher outro contacto da ficha</option>
                {(assign.contactos || []).map((c, i) => (
                  <option key={`${c.email}-${i}`} value={c.email}>
                    {(c.nome || c.email) + (c.departamento ? ` [${c.departamento}]` : "") + (c.principal ? " *" : "") + " - " + c.email}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3"><Btn variant="ghost" onClick={() => setAssign(null)}>Cancelar</Btn><Btn onClick={doAssign} testid="assign-save">Atribuir</Btn></div>
      </Modal>

      <Modal open={!!inviteEmail} onClose={() => setInviteEmail(null)} title="Enviar convite personalizado" testid="invite-email-modal">
        <Field label="Para (email herdado do pedido do agendamento)" value={inviteEmail?.para || ""} onChange={(v) => setInviteEmail({ ...inviteEmail, para: v })} placeholder="cliente@empresa.pt" testid="invite-email-para" />
        {(inviteEmail?.contactos || []).length > 0 && (
          <div className="mb-2 -mt-1">
            <select
              value=""
              onChange={(e) => setInviteEmail({ ...inviteEmail, para: e.target.value })}
              data-testid="invite-email-picker"
              className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-xs text-muted2"
            >
              <option value="">... ou escolher outro contacto da ficha (por departamento)</option>
              {(inviteEmail.contactos || []).map((c, i) => (
                <option key={`${c.email}-${i}`} value={c.email}>
                  {(c.nome || c.email) + (c.departamento ? ` [${c.departamento}]` : "") + (c.principal ? " *" : "") + " - " + c.email}
                </option>
              ))}
            </select>
          </div>
        )}
        <Field label="Assunto" value={inviteEmail?.assunto || ""} onChange={(v) => setInviteEmail({ ...inviteEmail, assunto: v })} testid="invite-email-assunto" />
        <TextArea label="Corpo do email (editavel)" value={inviteEmail?.corpo || ""} onChange={(v) => setInviteEmail({ ...inviteEmail, corpo: v })} testid="invite-email-corpo" />
        <p className="text-[11px] text-muted2">O texto ja inclui a data/hora e link Teams (se houver). Personalize o corpo antes de enviar.</p>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setInviteEmail(null)}>Cancelar</Btn><Btn onClick={sendInviteEmail} testid="invite-email-send"><Send size={14} /> Enviar convite</Btn></div>
      </Modal>

      {/* Bulk assign modal */}
      <Modal open={!!bulkAssign} onClose={() => setBulkAssign(null)} title={`Atribuir ${selected.size} agendamento(s) em massa`} testid="bulk-assign-modal">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Membro da equipa</label>
          <select value={bulkAssign?.responsavel_id || ""} onChange={(e) => setBulkAssign({ ...bulkAssign, responsavel_id: e.target.value })} data-testid="bulk-assign-member" className="w-full px-3 py-2 border border-tint rounded-lg bg-white">
            <option value="">- Escolher -</option>
            {members.map((u) => <option key={u.id} value={u.id}>{u.nome || u.email} {u.email ? `(${u.email})` : ""}</option>)}
          </select>
          <p className="text-[11px] text-muted2 mt-1">O convite Teams sera reenviado para o novo responsavel em cada agendamento seleccionado. O email do cliente que ja estava no proprio agendamento e preservado.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-navy mt-3">
          <input type="checkbox" checked={!!bulkAssign?.invite_client} onChange={(e) => setBulkAssign({ ...bulkAssign, invite_client: e.target.checked })} data-testid="bulk-invite-client" />
          Manter cliente convidado (email guardado em cada agendamento)
        </label>
        <div className="flex justify-end gap-2 pt-3"><Btn variant="ghost" onClick={() => setBulkAssign(null)}>Cancelar</Btn><Btn onClick={doBulkAssign} testid="bulk-assign-save">Atribuir a todos</Btn></div>
      </Modal>
    </div>
  );
}
